import { Messages } from '@/components/Messages';
import { OptimizerVersion } from '@/components/OptimizerVersion';
import { BudgetShareByCategory } from '@/dashboards/BudgetOptimization/components/BudgetShareByCategory';
import { useBudgetOptimizationContext } from '@/dashboards/BudgetOptimization/hooks/useBudgetOptimizationContext';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import {
    Can,
    FEATURE_FLAG_ACTIONS,
    FEATURE_FLAG_SUBJECTS,
} from '@/features/Casl';
import { BudgetChangeByVariable } from './BudgetChangeByVariable';
import { ContributionSummary } from './ContributionSummary';
import { ModifyBudget } from './ModifyBudget';
import { MonthlyMediaInvestment } from './MonthlyMediaInvestment';
import { ROICurves } from './ROICurves';
import { SuggestedBudgetTable } from './SuggestedBudgetTable';

export const BudgetOptimizationSection = ({
    onOutOfBoundsChannel,
    modalResetKey,
}: {
    onOutOfBoundsChannel?: (info: {
        channelName: string;
        min: number;
        max: number;
    }) => void;
    modalResetKey?: number;
}) => {
    const { messages, flushMessages } = useBudgetOptimizationContext();
    const {
        project: { projectLead },
    } = useDashboardGlobalContext();

    const showMessages = messages.length > 0;

    return (
        <div className="flex flex-wrap w-full max-2xl:gap-4">
            {showMessages && (
                <Messages
                    messages={messages}
                    onDismiss={flushMessages}
                    projectLeadEmail={projectLead.email}
                />
            )}
            <div className="flex flex-col w-1/2 max-2xl:w-full max-2xl:pr-0 gap-4 pr-2">
                <Can
                    I={FEATURE_FLAG_ACTIONS.VIEW}
                    a={
                        FEATURE_FLAG_SUBJECTS.BUDGET_OPTIMIZATION_DASHBOARD_MODIFY_BUDGET
                    }
                >
                    <div className="hidden max-2xl:block">
                        <ModifyBudget
                            onOutOfBoundsChannel={onOutOfBoundsChannel}
                            modalResetKey={modalResetKey}
                        />
                    </div>
                </Can>
                <Can
                    I={FEATURE_FLAG_ACTIONS.VIEW}
                    a={
                        FEATURE_FLAG_SUBJECTS.BUDGET_OPTIMIZATION_DASHBOARD_CONTRIBUTION_SUMMARY
                    }
                >
                    <ContributionSummary />
                </Can>
                <Can
                    I={FEATURE_FLAG_ACTIONS.VIEW}
                    a={
                        FEATURE_FLAG_SUBJECTS.BUDGET_OPTIMIZATION_DASHBOARD_SUGGESTED_BUDGET_ALLOCATION
                    }
                >
                    <SuggestedBudgetTable />
                </Can>
                <Can
                    I={FEATURE_FLAG_ACTIONS.VIEW}
                    a={
                        FEATURE_FLAG_SUBJECTS.BUDGET_OPTIMIZATION_DASHBOARD_ROI_CURVES
                    }
                >
                    <ROICurves />
                </Can>
                <Can
                    I={FEATURE_FLAG_ACTIONS.VIEW}
                    a={
                        FEATURE_FLAG_SUBJECTS.BUDGET_OPTIMIZATION_DASHBOARD_MONTHLY_MEDIA_INVESTMENT
                    }
                >
                    <MonthlyMediaInvestment />
                </Can>
            </div>
            <div className="flex flex-col w-1/2 max-2xl:w-full max-2xl:pr-0 gap-4 pl-2">
                <Can
                    I={FEATURE_FLAG_ACTIONS.VIEW}
                    a={
                        FEATURE_FLAG_SUBJECTS.BUDGET_OPTIMIZATION_DASHBOARD_MODIFY_BUDGET
                    }
                >
                    <div className="block max-2xl:hidden">
                        <ModifyBudget
                            onOutOfBoundsChannel={onOutOfBoundsChannel}
                            modalResetKey={modalResetKey}
                        />
                    </div>
                </Can>
                <Can
                    I={FEATURE_FLAG_ACTIONS.VIEW}
                    a={
                        FEATURE_FLAG_SUBJECTS.BUDGET_OPTIMIZATION_DASHBOARD_BUDGET_SHARE
                    }
                >
                    <div className="glass-tile p-6 flex flex-col gap-4">
                        <BudgetShareByCategory />
                        <BudgetChangeByVariable />
                    </div>
                </Can>
            </div>
            <div className="w-full md:mt-4 mt-0">
                <OptimizerVersion />
            </div>
        </div>
    );
};
